import gsap from "gsap"
import Lenis from "lenis"
import ScrollTrigger from 'gsap/ScrollTrigger'
import CustomEase from 'gsap/CustomEase'
import { Fancybox } from "@fancyapps/ui/dist/fancybox/";

gsap.registerPlugin(ScrollTrigger, CustomEase);

export default class Gallery {
    constructor(options = {}) {
        this.lightboxLenis = null
        this.scroll = options.scroll
        this.clickedIndex = 0
        this.galleryImages = []
        
        this.init()
    }

    isMobile() {
        return window.innerWidth <= 1024 || /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
    }

    init() {
        const gallerySlides = document.querySelectorAll(".galleries__content .gallery-slide")
        
        gallerySlides.forEach((slide, index) => {
            slide.style.cursor = "pointer"
            slide.addEventListener("click", (e) => {
                e.preventDefault()

                const dataIndex = slide.dataset.index
                this.clickedIndex = dataIndex !== undefined && dataIndex !== null ? parseInt(dataIndex) : index
                this.openLightbox()
            })
        })
    }

    openLightbox() {
        this.createLightboxElement()
        this.animateLightboxOpen()
        
        const content = this.fetchContent()
        this.renderContent(content)
        this.initLightboxScroll()
        this.attachCloseHandlers()
    }

    createLightboxElement() {
        const lightbox = document.createElement("div")
        lightbox.className = "gallery"
        lightbox.innerHTML = `
            <div class="gallery__overlay"></div>
            <div class="gallery__content">
                <div class="gallery__loader">
                    
                </div>
            </div>
        `
        document.body.appendChild(lightbox)
        
        if (this.isMobile()) {
            // Mobile: Lock body scroll - save scroll position
            this.scrollPosition = window.pageYOffset
            document.body.style.position = 'fixed'
            document.body.style.top = `-${this.scrollPosition}px`
            document.body.style.width = '100%'
        } else {
            // Desktop: Use Lenis stop
            document.body.style.overflow = "hidden"
            if (this.scroll) {
                this.scroll.stop()
            }
        }
    }

    animateLightboxOpen() {
        gsap.fromTo(".gallery__overlay", 
            { 
                scaleY: 0,
                transformOrigin: "bottom"
            },
            { 
                scaleY: 1,
                duration: 0.8,
                ease: "power3.out"
            }
        )
        
        gsap.fromTo(".gallery__content",
            {
                y: "100%",
                opacity: 0
            },
            {
                y: 0,
                opacity: 1,
                duration: 0.8,
                ease: "power3.out",
                delay: 0.2
            }
        )
    }

    fetchContent() {
        // Get gallery data from DOM elements instead of WordPress API
        const gallerySlides = document.querySelectorAll(".galleries__content .gallery-slide")
        
        this.galleryImages = Array.from(gallerySlides).map(slide => ({
            url: slide.dataset.imageUrl || '',
            caption: slide.dataset.caption || '',
            originalIndex: parseInt(slide.dataset.index) || 0
        }))
        
        return { galleryImages: this.galleryImages }
    }

    renderContent(page) {
        const reorderedImages = this.reorderImages(this.galleryImages, this.clickedIndex)
        
        const imagesHTML = reorderedImages.map(image => {
            // Check if the URL is a video (mp4 or webm)
            const isVideo = /\.(mp4|webm)(\?.*)?$/i.test(image.url)
            
            if (isVideo) {
                return `
                    <div data-fancybox="gallery" href="${image.url}" data-caption="${image.caption}">
                        <div class="icon-video">
                            <svg width="10" height="18" viewBox="0 0 10 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M9.75 8.66028L1.38009e-07 17.3205L8.95112e-07 2.34157e-05L9.75 8.66028Z" fill="white"/>
                            </svg>
                        </div>
                        <video src="${image.url}" preload="metadata"></video>
                    </div>
                `
            } else {
                return `
                    <div data-fancybox="gallery" href="${image.url}" data-caption="${image.caption}">
                        <img src="${image.url}" alt="${image.caption}" />
                    </div>
                `
            }
        }).join('')
        
        const lightboxContent = document.querySelector(".gallery__content")
        lightboxContent.innerHTML = `
            <button class="gallery__close">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <line x1="2" y1="2" x2="22" y2="22" stroke="currentColor" stroke-width="2"/>
                    <line x1="22" y1="2" x2="2" y2="22" stroke="currentColor" stroke-width="2"/>
                </svg>
            </button>
            <div class="gallery__inner">
                <div class="gallery__header">
                    <h3>Gallery</h3>
                    <span class="eyebrow eyebrow--light">${reorderedImages.length} imágenes</span>
                </div>
                <div class="gallery__body">
                    ${imagesHTML}
                </div>
            </div>
        `
        
        gsap.from(".gallery__inner, .gallery__sidebar", {
            y: 30,
            opacity: 0,
            duration: 0.5,
            stagger: 0.2,
            ease: "power2.out"
        })

        Fancybox.bind("[data-fancybox]", {
            
        });
    }

    reorderImages(images, clickedIndex) {
        if (!images || images.length === 0) return []
        
        const reordered = []
        
        // Find the clicked image by its original index (not array position)
        const clickedImage = images.find(img => img.originalIndex === clickedIndex)
        if (clickedImage) {
            reordered.push(clickedImage)
        }
        
        images.forEach((image) => {
            if (image.originalIndex !== clickedIndex) {
                reordered.push(image)
            }
        })
        
        return reordered
    }

    renderError(error) {
        console.error('Error fetching data:', error)
        const lightboxContent = document.querySelector(".gallery__content")
        lightboxContent.innerHTML = `
            <button class="event__close">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <line x1="2" y1="2" x2="22" y2="22" stroke="currentColor" stroke-width="2"/>
                    <line x1="22" y1="2" x2="2" y2="22" stroke="currentColor" stroke-width="2"/>
                </svg>
            </button>
            <div class="gallery__inner">
                <h2>Error</h2>
                <p>Failed to load content. Please try again.</p>
            </div>
        `
        
        this.attachCloseHandlers()
    }

    initLightboxScroll() {
        const lightboxInner = document.querySelector(".gallery__inner")
        this.lightboxLenis = new Lenis({
            wrapper: lightboxInner,
            content: lightboxInner,
            duration: 1.2,
            easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
            orientation: "vertical",
            gestureOrientation: "vertical",
            smoothWheel: true,
            wheelMultiplier: 1,
            smoothTouch: false,
            touchMultiplier: 2,
            infinite: false,
        })
        
        const rafLightbox = (time) => {
            if (this.lightboxLenis) {
                this.lightboxLenis.raf(time)
                requestAnimationFrame(rafLightbox)
            }
        }
        requestAnimationFrame(rafLightbox)
    }

    attachCloseHandlers() {
        const closeButton = document.querySelector(".gallery__close")
        const overlay = document.querySelector(".gallery__overlay")
        
        if (closeButton) {
            closeButton.addEventListener("click", () => this.closeLightbox())
        }
        
        if (overlay) {
            overlay.addEventListener("click", () => this.closeLightbox())
        }
    }

    closeLightbox() {
        if (this.lightboxLenis) {
            this.lightboxLenis.destroy()
            this.lightboxLenis = null
        }
        
        gsap.to(".gallery__overlay", {
            scaleY: 0,
            transformOrigin: "bottom",
            duration: 0.6,
            ease: "power3.in"
        })
        
        gsap.to(".gallery__content", {
            y: "100%",
            opacity: 0,
            duration: 0.6,
            ease: "power3.in",
            onComplete: () => {
                document.querySelector(".gallery")?.remove()
                
                if (this.isMobile()) {
                    // Mobile: Unlock body scroll - restore scroll position
                    document.body.style.position = ''
                    document.body.style.top = ''
                    document.body.style.width = ''
                    window.scrollTo(0, this.scrollPosition)
                } else {
                    // Desktop: Restore Lenis
                    document.body.style.overflow = ""
                    if (this.scroll) {
                        this.scroll.start()
                    }
                }
            }
        })
    }
}

function initGallerySectionAnimation() {
    const galleriesSection = document.querySelector('.galleries')
    
    if (!galleriesSection) return
    
    const customEase = CustomEase.create("out", "0.125, 0.425, 0.270, 1.000")
    
    
    const slides = galleriesSection.querySelectorAll('.swiper-slide')
    const swiperMenu = galleriesSection.querySelector('.swiper-menu')
    const button = galleriesSection.querySelector('.galleries__actions .button')
    
    
    if (slides.length > 0) {
        slides.forEach(slide => {
            const isPrev = slide.classList.contains('swiper-slide-prev')
            const isNext = slide.classList.contains('swiper-slide-next')
            
            gsap.set(slide, { position: 'relative' })
            
            if (isPrev) {
                gsap.set(slide, { left: '80px', top: 0, autoAlpha: 0 })
            } else if (isNext) {
                gsap.set(slide, { left: '-80px', top: 0, autoAlpha: 0 })
            } else {
                gsap.set(slide, { top: '80px', left: 0, autoAlpha: 0 })
            }
        })
    }
    
    if (swiperMenu) {
        const menuElements = [
            swiperMenu.querySelector('.swiper-prev'),
            swiperMenu.querySelector('.swiper-dots'),
            swiperMenu.querySelector('.swiper-next')
        ].filter(el => el !== null)
        
        gsap.set(menuElements, { autoAlpha: 0, y: 30 })
    }
    
    if (button) {
        gsap.set(button, { autoAlpha: 0, y: 50 })
    }
    
    if (slides.length > 0) {
        slides.forEach((slide, index) => {
            const isPrev = slide.classList.contains('swiper-slide-prev')
            const isNext = slide.classList.contains('swiper-slide-next')
            
            let fromState, toState
            
            if (isPrev) {
                fromState = { left: '80px', top: 0, autoAlpha: 0 }
                toState = { left: '0px', top: 0, autoAlpha: 1 }
            } else if (isNext) {
                fromState = { left: '-80px', top: 0, autoAlpha: 0 }
                toState = { left: '0px', top: 0, autoAlpha: 1 }
            } else {
                fromState = { top: '80px', left: 0, autoAlpha: 0 }
                toState = { top: '0px', left: 0, autoAlpha: 1 }
            }
            
            gsap.fromTo(slide, 
                fromState,
                {
                    ...toState,
                    duration: 1,
                    delay: index * 0.1,
                    ease: customEase,
                    scrollTrigger: {
                        trigger: '.galleries__content',
                        start: 'clamp(top 85%)',
                        once: true
                    }
                }
            )
        })
    }
    
    if (swiperMenu) {
        const menuElements = [
            swiperMenu.querySelector('.swiper-prev'),
            swiperMenu.querySelector('.swiper-dots'),
            swiperMenu.querySelector('.swiper-next')
        ].filter(el => el !== null)
        
        gsap.fromTo(menuElements,
            {
                autoAlpha: 0,
                y: 30
            },
            {
                autoAlpha: 1,
                y: 0,
                duration: 0.8,
                stagger: 0.1,
                ease: customEase,
                scrollTrigger: {
                    trigger: '.galleries__content',
                    start: 'clamp(top 85%)',
                    once: true
                },
                delay: 0.4
            }
        )
    }
    
    if (button) {
        gsap.fromTo(button,
            {
                autoAlpha: 0,
                y: 50
            },
            {
                autoAlpha: 1,
                y: 0,
                duration: 0.9,
                ease: customEase,
                scrollTrigger: {
                    trigger: '.galleries__actions',
                    start: 'clamp(top 90%)',
                    once: true
                }
            }
        )
    }
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initGallerySectionAnimation)
} else {
    initGallerySectionAnimation()
}